/* globals INITIAL_STATE */
import * as ActionTypes from '../actions/FinalizePurchaseActions';
import * as InitializePurchaseActionTypes from '../actions/InitializePurchaseActions';
import * as RefinePurchaseActionTypes from '../actions/RefinePurchaseActions';
import * as InstapayActionTypes from '../actions/InstapayActions';
import * as CartActionTypes from 'cart/actions/CartActions';
import { reducedState } from '../utils/GlobalUtils';
import { rupees } from 'config/utils';
import getLinkInitialState from './finalize/linkInitialState';
import getCartInitialState from './finalize/cartInitialState';
import getOfferInitialState from './finalize/offerInitialState';
import getInstapayInitialState from './finalize/instapayInitialState';
import sanitizePaymentOptions, {
  resetPaymentOptions,
} from './finalize/paymentOptions';
import { getPaymentOptionKey } from 'payments/utils/FinalizePurchaseUtils';

function finalizePurchase (state = getInitialState(INITIAL_STATE), action) {
  switch (action.type) {
    case ActionTypes.HANDLE_SELECT_PAYMENT_OPTION: {
      const { payload: { selectedOption: { type, name, selectedSavedCard } } } = action;
      return {
        ...state,
        selectedPaymentMode: type,
        selectedPaymentObject: state.paymentOptions[getPaymentOptionKey({ type, name })],
        isSelectedPaymentModeLoading: ['nb', 'card', 'wallet', 'saved_cards'].indexOf(type) !== -1,
        selectedSavedCard,
      };
    }

    case ActionTypes.HANDLE_SELECT_BANK: {
      const { payload: { bankId } } = action;
      return {
        ...state,
        selectedBankId: bankId || '',
        errors: {},
      };
    }

    case ActionTypes.HANDLE_SELECT_WALLET: {
      const { payload: { walletId } } = action;
      return {
        ...state,
        selectedWalletId: walletId || '',
        errors: {},
      };
    }

    case ActionTypes.HANDLE_BANK_PAYMENT_FAILURE: {
      return {
        ...state,
        errors: {
          selectedBank: ['Please Choose a Bank to Pay'],
        },
      };
    }

    case ActionTypes.HANDLE_BANK_PAYMENT_SUCCESS: {
      return {
        ...state,
        isBankPaymentRequestSubmitted: true,
      };
    }

    case ActionTypes.HANDLE_WALLET_PAYMENT_FAILURE: {
      return {
        ...state,
        errors: {
          selectedWallet: ['Please Choose a Wallet to Pay'],
        },
      };
    }

    case ActionTypes.HANDLE_WALLET_PAYMENT_SUCCESS: {
      return {
        ...state,
        isWalletPaymentRequestSubmitted: true,
      };
    }

    case ActionTypes.STORE_FINAL_PAYLOAD: {
      const { finalPayload } = action.payload;
      return {
        ...state,
        finalPayload,
      };
    }

    case ActionTypes.HANDLE_CHANGE_EMI_CARD_PROVIDER: {
      const { bankKey, paymentOption } = action.payload;
      const { emiList } = state.paymentOptions[getPaymentOptionKey(paymentOption)];
      const emiTenure = Object.keys(emiList[bankKey].rates)[0];
      return {
        ...state,
        selectedEMICardProvider: bankKey,
        selectedEMITenure: emiTenure,
        emiAmount: emiList[bankKey].rates[emiTenure],
      };
    }

    case ActionTypes.HANDLE_CHANGE_EMI_TENURE: {
      const { tenure, emiAmount } = action.payload;
      return {
        ...state,
        selectedEMITenure: tenure,
        emiAmount,
      };
    }

    case ActionTypes.HANDLE_SUBMIT_EMI_FORM_SUCCESS: {
      return {
        ...state,
        isEMIFormSubmitted: true,
        isSelectedPaymentModeLoading: true,
      };
    }

    case ActionTypes.HANDLE_CHANGE_SELECTED_EMI_PROVIDER: {
      return {
        ...state,
        isEMIFormSubmitted: false,
        emiIframeUri: '',
      };
    }

    case ActionTypes.HANDLE_TOGGLE_LOADER: {
      return {
        ...state,
        isSelectedPaymentModeLoading: !state.isSelectedPaymentModeLoading,
      };
    }

    case ActionTypes.HANDLE_RESET_SELECTED_PAYMENT_MODE: {
      return {
        ...state,
        selectedPaymentMode: '',
        selectedPaymentModeName: '',
        selectedBankId: '',
        isBankPaymentRequestSubmitted: false,
        selectedWalletId: '',
        isWalletPaymentRequestSubmitted: false,
        isUPIRequestSubmitted: false,
        upiHandle: '',
        upiHandleError: '',
      };
    }

    case ActionTypes.HANDLE_CHANGE_UPI_HANDLE: {
      return {
        ...state,
        upiHandle: action.payload.handle,
        upiHandleError: '',
      };
    }

    case ActionTypes.HANDLE_SUBMIT_UPI_REQUEST: {
      return {
        ...state,
        isUPIRequestSubmitted: false,
        isUPIRequestSubmitting: true,
      };
    }

    case ActionTypes.HANDLE_SUBMIT_UPI_FAILURE: {
      try {
        const { statusCode } = action.errors;
        if (statusCode !== 2) {
          return {
            ...state,
            upiHandleError: 'Sorry. Please try again!',
            isUPIRequestSubmitting: false,
          };
        }
      } catch (e) {
        return {
          ...state,
          upiHandleError: action.errors.virtualAddress,
          isUPIRequestSubmitting: false,
        };
      }
    }

    case ActionTypes.HANDLE_SUBMIT_UPI_SUCCESS: {
      const { statusCode, paymentId, upiBank } = action.response;
      if (statusCode !== 2) {
        return {
          ...state,
          upiHandleError: 'Sorry. Please try again!',
          isUPIRequestSubmitting: false,
        };
      }
      // considered a successful request
      return {
        ...state,
        upiBank,
        paymentId,
        isUPIRequestSubmitted: true,
        isUPIRequestSubmitting: false,
      };
    }

    case InitializePurchaseActionTypes.HANDLE_IP_CHANGE_INPUT: {
      if (!state.offer) {
        // CART
        return state;
      }

      const { payload: { changedData } } = action;
      const changedKey = Object.keys(changedData)[0];
      if (
        !state.isLinkPayment &&
        !state.isInstapayPayment &&
        state.offer.variantAmountMap[changedKey] ||
        changedKey === 'quantity'
      ) {
        return reducedState(state, changedData);
      }
      return state;
    }

    case InitializePurchaseActionTypes.HANDLE_APPLY_DISCOUNT_SUCCESS: {
      const { discount: { amountOff } } = action.response;
      return reducedState(state, { discountAmount: Number(amountOff) });
    }

    case InitializePurchaseActionTypes.HANDLE_REMOVE_DISCOUNT_SUCCESS: {
      return reducedState(state, { discountAmount: 0 });
    }

    case InitializePurchaseActionTypes.HANDLE_FETCH_SHIPPING_CHARGES_SUCCESS: {
      const { shippingCharges } = action.response;
      const shippingChargesFormatted = (shippingCharges) ? parseFloat(shippingCharges) : 0;
      return {
        ...state,
        totalAmountFormatted: rupees(parseFloat(state.totalAmount) + shippingChargesFormatted),
      };
    }

    case InitializePurchaseActionTypes.HANDLE_SET_TOTAL_AMOUNT: {
      const { totalAmount } = action.payload;
      let updateWith;
      if (totalAmount !== Math.round(totalAmount)) {
        updateWith = {
          ...state,
          totalAmount: totalAmount.toFixed(2),
        };
      }

      updateWith = {
        ...state,
        totalAmount,
      };

      return {
        ...updateWith,
        totalAmountFormatted: rupees(updateWith.totalAmount),
      };
    }

    case InitializePurchaseActionTypes.HANDLE_SUBMIT_INITIALIZE_PURCHASE_SUCCESS:
    case RefinePurchaseActionTypes.HANDLE_SUBMIT_REFINE_PURCHASE_SUCCESS:
      const { details } = action.response;
      const paymentOptions = {
        ...state.paymentOptions,
        ...details,
      };

      return {
        ...state,
        ...resetPaymentOptions,
        totalAmount: Number(details.totalAmount),
        ...sanitizePaymentOptions(paymentOptions),
      };

    case InstapayActionTypes.HANDLE_SUBMIT_INSTAPAY_SUCCESS: {
      return {
        ...state,
        totalAmount: action.payload.amount,
        totalAmountFormatted: rupees(action.payload.amount),
      };
    }

    case CartActionTypes.HANDLE_SUBMIT_CART_SUCCESS: {
      const { totalAmount } = action.response.cart;
      return {
        ...state,
        totalAmount: Number(totalAmount),
        totalAmountFormatted: rupees(totalAmount),
      };
    }

    case CartActionTypes.HANDLE_CHECKOUT_CART_SUCCESS: {
      const paymentOptions = { ...action.response.details };
      return {
        ...state,
        ...resetPaymentOptions,
        ...sanitizePaymentOptions(paymentOptions),
      };
    }

    default:
      return state;
  }
}

export default finalizePurchase;


export const baseInitialState = {
  form: {},
  paymentOptions: {},
  selectedPaymentMode: '',
  selectedSavedCard: '',
  selectedBankId: '',
  isBankPaymentRequestSubmitted: false,
  selectedWalletId: '',
  isWalletPaymentRequestSubmitted: false,
  errors: {},
  finalPayload: {},
  selectedEMICardProvider: '',
  isEMIFormSubmitted: false,
  emiIframeUri: '',
  isSelectedPaymentModeLoading: false,
  upiHandle: '',
};

const getInitialState = (state) => {
  const {
    should_retry: shouldRetry,
    response_type: responseType,
  } = state;

  if (state.is_cart_enabled || (responseType == 'cart' && shouldRetry)) {
    // cart feature to be enabled only on instamojo.com
    if ( ((!state.offer || (state.offer && state.offer.is_cartable)) && !state.is_embed) ||
      ((responseType == 'cart' && shouldRetry) && !state.retry_data.is_embed) ) {
      return getCartInitialState(state, baseInitialState);
    }
  }

  if (state.is_link_payment || (responseType == 'link' && shouldRetry)) {
    return getLinkInitialState(state, baseInitialState);
  }

  if (state.is_instapay_payment || (responseType == 'instapay' && shouldRetry)) {
    return getInstapayInitialState(state, baseInitialState);
  }

  if (state.offer || (responseType == 'product' && shouldRetry)) {
    return getOfferInitialState(state, baseInitialState);
  }

  return {};
};



// WEBPACK FOOTER //
// ./src/payments/reducers/FinalizePurchaseReducers.js